/**
 * DraftingPage — top-level Drafting page (AB#1573, AB#1570, AB#1347).
 *
 * Surfaces drafts AND strategies (the user-facing label for what the
 * backend calls "insights") under a single URL: /drafting. A draft or
 * strategy can be standalone or attached to one or more matters /
 * projects via the polymorphic draft_attachments / insight_attachments
 * join tables introduced on the backend side.
 *
 * URL shape:
 *   /drafting                    → merged list of Documents + Strategies
 *   /drafting?add=true&kind=…    → creation wizard for the chosen kind
 *   /drafting/:uuid              → detail view (kind read from the row)
 *
 * The list is unified — everything is a "draft"; the two creatable kinds are
 * a Document (kind='draft') or a Strategy (kind='strategy'), chosen at
 * creation time and surfaced only as a filter in the list.
 */

import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import DraftingList from '../components/Drafting/DraftingList';
import DraftingDetail from '../components/Drafting/DraftingDetail';
import DraftingWizard from '../components/Drafting/DraftingWizard';
import { LayoutContext } from '../contexts/LayoutContext';

export type DraftingKind = 'draft' | 'strategy';

function DraftingPage() {
  const { draftingUuid } = useParams<{ draftingUuid?: string }>();
  const history = useHistory();
  const location = useLocation();

  // The list/detail views own their internal scroll (fixed toolbar + scrolling
  // body), so disable the outer layout scroll — mirrors the Tables page.
  const { setIsContentScrollable } = useContext(LayoutContext);
  useEffect(() => {
    setIsContentScrollable(false);
    return () => { setIsContentScrollable(true); };
  }, [setIsContentScrollable]);

  const params = new URLSearchParams(location.search);
  const isCreating = params.get('add') === 'true';

  if (draftingUuid) {
    // Detail mode — the document's own row carries its kind, so the page
    // doesn't need to know up front whether it's a document or a strategy.
    return <DraftingDetail draftingUuid={draftingUuid} />;
  }

  if (isCreating) {
    // Kind is chosen in the wizard's first step now.
    return (
      <DraftingWizard
        onCreated={(uuid) => { history.push(`/drafting/${uuid}`); }}
        onCancel={() => history.replace('/drafting')}
        initialMatterUuid={params.get('matterUuid')}
      />
    );
  }

  // Merged list — creating opens the wizard (which asks Document vs Strategy).
  return (
    <DraftingList
      onNew={() => history.push('/drafting?add=true')}
    />
  );
}

export default DraftingPage;
